import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { noop } from 'lodash';
import { FaExternalLinkAlt } from 'react-icons/fa';

import { SubscriptionRequestErrors } from '@/types/subscriptions/request';
import {
  getTermsUrls,
  isEMBA,
  pickServiceGroupId,
  SubscriptionDetail,
} from '@/types/subscriptions/subscription';
import { getFullName } from '@/utils/name';
import { startDateFormat } from '@/utils/date';
import { BackLink } from './BackLink';
import { ContactPreview } from './ContactPreview';
import { UsersPreview } from './UsersPreview';
import { Warning } from './Warning';
import { isSubscriptionHodaiPlus } from '../Form/util';
import { SharedButton } from '@/components/shared/Button';

export type Props = {
  mode: 'new' | 'detail' | 'edit';
  onBack?: () => void; // mode === 'detail' の場合渡さない
  subscription: SubscriptionDetail;
  isConnecting: boolean;
  onSubmit?: () => void;
  warnings?: SubscriptionRequestErrors | null;
};

export const Preview: React.FC<Props> = ({
  mode,
  onBack = noop,
  isConnecting,
  onSubmit = noop,
  subscription,
  warnings = null,
}) => {
  const { t } = useTranslation();
  const serviceGroupId = pickServiceGroupId(subscription);
  const termsURLs = getTermsUrls(subscription);
  let serviceLabel = t('models.subscription.term');

  if (serviceGroupId && isEMBA(serviceGroupId)) {
    serviceLabel = t('models.subscription.subject');
  }

  return (
    <Styled>
      <div className="subscription-content">
        <div className="subscription-content-raw">
          <div>
            <label className="label">
              {serviceLabel}
              <BackLink mode={mode} hash="services" onClick={onBack} />
            </label>
            <ul className="content-column-value">
              {subscription.services.map((s, i) => (
                <li key={s.id} data-test={`confirm-service-${i}`}>
                  {s.name}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <label className="label">
              {t('models.subscription.startsOn')}
              <BackLink mode={mode} onClick={onBack} hash="startsOn" />
            </label>
            <div data-test="confirm-starts-on" className="content-column-value">
              {startDateFormat({
                date: subscription.startsOn,
                withWeekDay: true,
              })}
            </div>
          </div>
        </div>
        <div className="subscription-content-raw">
          <div>
            <label className="label">
              {isSubscriptionHodaiPlus(serviceGroupId)
                ? t('models.subscription.adminsAtHodaiPlus')
                : t('models.subscription.admins')}
              <BackLink mode={mode} onClick={onBack} hash="adminIds" />
            </label>
            <ul className="content-column-value">
              {subscription.admins.map((a, i) => (
                <li data-test={`confirm-admin-${i}`} key={a.id}>
                  {getFullName(a.firstName, a.lastName)}
                </li>
              ))}
            </ul>
          </div>
        </div>
        {subscription.projectClass && (
          <div className="content-project-class">
            <label className="label">
              {t('models.subscription.projectClass')}
              <BackLink mode={mode} onClick={onBack} hash="projectClass" />
            </label>
            <div className="content-project-class-value">
              {subscription.projectClass
                ? subscription.projectClass.name
                : t('ui.expression.notApplicable')}
            </div>
          </div>
        )}
        <label className="label">
          {t('models.subscription.users')}
          <span className="users-preview-students-number">
            （{subscription.users.length} {t('ui.unit.studentNumber')}）
          </span>
          <BackLink mode={mode} onClick={onBack} hash="users" />
        </label>
        <UsersPreview users={subscription.users} warnings={warnings} />
        <ContactPreview
          subscription={subscription}
          mode={mode}
          onBack={onBack}
        />
        {mode !== 'detail' && (
          <>
            <div className="l-warning">
              <Warning />
            </div>
            <div className="content-submit">
              {termsURLs.map((entry, i) => (
                <div className="terms-of-service" key={i}>
                  <a
                    href={entry.url}
                    rel="noopener noreferrer"
                    target="_blank"
                    className="terms-of-external-link"
                  >
                    {entry.label}
                    <FaExternalLinkAlt className="icon" />
                  </a>
                </div>
              ))}
              <SharedButton
                dataTest="confirm-submit"
                dataGtm="submit-subscription"
                disabled={isConnecting}
                onClick={onSubmit}
                fontSize="small"
              >
                {mode === 'new' && t('ui.action.withAgreement.subscribe')}
                {mode === 'edit' && t('ui.action.withAgreement.change')}
              </SharedButton>
            </div>
          </>
        )}
      </div>
    </Styled>
  );
};

const Styled = styled.div`
  .subscription-content-raw {
    display: flex;
    justify-content: space-between;

    margin-bottom: 4em;

    > div {
      width: 50%;
    }
  }

  .content-column-value {
    padding: 0 0 0 0.5em;
    margin: 0.7em 0 0;
    list-style: none;
  }

  .content-project-class {
    margin-bottom: 3em;
  }

  .content-project-class-value {
    margin: 0.7em 0 0;
  }

  .content-billing-note-value {
    margin: 0.7em 0 0;
    overflow-wrap: break-word;
    word-wrap: break-word;
  }

  .content-contact-preview {
    margin-bottom: 3em;
  }

  .content-submit {
    width: fit-content;
    margin: 0 auto;
    text-align: center;
  }

  .terms-of-service {
    margin-bottom: 1em;

    .terms-of-external-link {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 5px;

      .icon {
        width: 16px;
        height: 16px;
      }
    }
  }

  .l-warning {
    margin: -1.3em 0 3.2em;
  }

  .label {
    display: flex;
  }
`;
